import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormBuilder,
    Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Observable, throwError } from 'rxjs';
import { catchError, first, switchMap, tap } from 'rxjs/operators';
import { isAPIApplicationException } from '../../../../core/helpers';
import { LanguagesService } from '../../../../core/services/languages.service';
import { ChangesNotSavedComponent } from '../../../../shared/components/changes-not-saved';
import { User } from '../../../auth/models';
import { AuthService } from '../../../auth/services';
import { SecurityCodeComponent } from '../../components';

@Component({
    selector: 'app-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrls: ['./personal-information.component.scss'],
    standalone: true,
    imports: [
        TranslocoModule,
        CommonModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        FlexLayoutModule,
        MatButtonModule,
        MatInputModule,
        MatRadioModule
    ]
})
export class PersonalInformationComponent
    extends ChangesNotSavedComponent
    implements OnInit
{
    personalInformation = this._fb.group({
        userId: [undefined, Validators.required],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        emailOrPhoneNumber: [undefined, Validators.required],
        preferredLanguageId: ['', Validators.required]
    });
    submitted = false; // because we are extending ChangesNotSavedComponent
    languages$ = this._languages.languages$;

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly _snackBar: MatSnackBar,
        private readonly _transloco: TranslocoService,
        private readonly _auth: AuthService,
        private readonly _languages: LanguagesService,
        private readonly _dialog: MatDialog
    ) {
        super(); // because we are extending ChangesNotSavedComponent
        document
            .getElementsByTagName('body')[0]
            .classList.remove('blue-background-active');
    }

    ngOnInit(): void {
        this._auth.user$.subscribe((user) =>
            this.personalInformation.patchValue({ ...user })
        );
    }

    get firstName(): AbstractControl | null {
        return this.personalInformation.get('firstName');
    }

    get lastName(): AbstractControl | null {
        return this.personalInformation.get('lastName');
    }

    get emailOrPhoneNumber(): AbstractControl | null {
        return this.personalInformation.get('emailOrPhoneNumber');
    }

    get preferredLanguageId(): AbstractControl | null {
        return this.personalInformation.get('preferredLanguageId');
    }

    onSubmit(event: any): void {
        if (this.personalInformation.valid) {
            if (this.emailOrPhoneNumber?.dirty) {
                let dialog: MatDialogRef<SecurityCodeComponent>;
                this._auth
                    .requestUpdateUserCode(
                        this.personalInformation.get('userId')?.value,
                        this.emailOrPhoneNumber.value
                    )
                    .pipe(
                        catchError((e) => this.handleCodeRequestError(e)),
                        switchMap(() => {
                            // Use `width`, not `maxWidth`: styles.scss sets
                            // `.cdk-overlay-pane { max-width: 950px !important }` globally,
                            // which beats the inline max-width this config would produce, so
                            // a maxWidth here is silently ignored and the dialog renders at
                            // 950px. No minHeight — it only added empty space below the code.
                            dialog = this._dialog.open(SecurityCodeComponent, {
                                width: '520px'
                            });
                            return dialog.afterClosed();
                        })
                    )
                    .subscribe(() => {
                        if (dialog.componentInstance.complete) {
                            // Hand the freshly entered code straight to the request; it is
                            // never stored, so this is the only place it exists.
                            this.updateUser(
                                dialog.componentInstance.code as number
                            );
                        }
                    });
            } else {
                this.updateUser();
            }
        }
    }

    isChanged(): boolean {
        return this.personalInformation.touched;
    }

    isSaved(): boolean {
        return this.submitted;
    }

    /**
     * @param securityCode only supplied when the email or phone number changed, which is the
     * one case the API validates a code for.
     */
    private updateUser(securityCode?: number) {
        const updatedUser = new User({
            ...this.personalInformation.value
        });
        this._auth
            .updateUser(updatedUser, securityCode)
            .pipe(
                switchMap(() =>
                    this._transloco
                        .selectTranslate('application.snack.saved')
                        .pipe(first())
                ),
                tap((message) => {
                    this._snackBar.open(message, undefined, {
                        duration: 4000
                    });
                    this.submitted = true;
                    window.location.reload();
                })
            )
            .subscribe();
    }

    private handleCodeRequestError(e: any): Observable<never> {
        if (isAPIApplicationException(e)) {
            this.emailOrPhoneNumber?.setErrors({
                invalidEmailOrPhoneNumber: true
            });
        }
        return throwError(e);
    }
}
